// src/components/tasks/TasksPage.tsx
import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  getDoc,
  doc
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Task } from '../../types/tasks';
import CreateTaskModal from './CreateTaskModal';
import TaskDetailsModal from './TaskDetailsModal';
import AppLayout from '../layout/AppLayout';
import toast from 'react-hot-toast';
import { Plus, Filter, Search } from 'lucide-react';
import { NotificationService } from '../../services/NotificationService';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export default function TasksPage() {
  useDocumentTitle('Tasks');
  
  const [tasks, setTasks] = useState<Task[]>([]);
  const [userMap, setUserMap] = useState<Record<string, { displayName: string; email: string }>>({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [statusFilter, setStatusFilter] = useState<Task['status'] | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<Task['priority'] | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) return;

    // Subscribe to tasks
    const unsubscribe = onSnapshot(
      query(collection(db, 'tasks'), orderBy('createdAt', 'desc')),
      async (snapshot) => {
        const fetched: Task[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as Task;
          fetched.push({ id: docSnap.id, ...data });
        });

        // Filter to only tasks we care about
        const visible = fetched.filter(
          (t) => t.assignedTo?.includes(currentUser.uid) || t.createdBy === currentUser.uid
        );
        setTasks(visible);
        setLoading(false);

        // Collect unique assignedTo UIDs
        const allUids = new Set<string>();
        visible.forEach((t) => t.assignedTo?.forEach((uid) => allUids.add(uid)));

        // Fetch each user once
        const entries = await Promise.all(
          Array.from(allUids).map(async (uid) => {
            const userSnap = await getDoc(doc(db, 'users', uid));
            if (userSnap.exists()) {
              const u = userSnap.data() as any;
              return [
                uid,
                {
                  displayName: u.fullName || u.displayName || u.email.split('@')[0] || 'Unknown',
                  email: u.email || '—'
                }
              ] as [string, { displayName: string; email: string }];
            } else {
              return [uid, { displayName: 'Unknown', email: '—' }] as [
                string,
                { displayName: string; email: string }
              ];
            }
          })
        );
        setUserMap(Object.fromEntries(entries));
      },
      (err) => {
        console.error('Error loading tasks:', err);
        toast.error('Failed to load tasks');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  const filtered = tasks.filter((t) => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (priorityFilter !== 'all' && t.priority !== priorityFilter) return false;
    
    // Search in title and description
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesTitle = t.title?.toLowerCase().includes(query);
      const matchesDescription = t.description?.toLowerCase().includes(query);
      if (!matchesTitle && !matchesDescription) return false;
    }
    
    return true;
  });

  const handleCreateTask = async (taskData: Partial<Task>) => {
    if (!currentUser) return;
    try {
      const taskRef = await addDoc(collection(db, 'tasks'), {
        ...taskData,
        createdBy: currentUser.uid,
        assignedTo: taskData.assignedTo || [],
        status: 'pending',
        notes: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Create notifications for assigned users
      if (taskData.assignedTo && taskData.assignedTo.length > 0) {
        const notificationPromises = taskData.assignedTo.map(userId => 
          NotificationService.createNotification({
            type: 'task',
            title: 'New Task Assigned',
            message: `You have been assigned a new task: "${taskData.title}"`,
            priority: taskData.priority === 'high' ? 'high' : taskData.priority === 'low' ? 'low' : 'medium',
            recipientId: userId,
            actionUrl: `/tasks`,
            read: false,
            metadata: {
              taskId: taskRef.id
            }
          })
        );

        await Promise.all(notificationPromises);
      }

      toast.success('Task created');
      setShowCreateModal(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to create task');
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-text-secondary">Loading tasks…</div>
        </div>
      </AppLayout>
    );
  }

  const columns: { key: Task['status'] | 'all'; label: string }[] = [
    { key: 'pending', label: 'Pending' },
    { key: 'in-progress', label: 'In Progress' },
    { key: 'completed', label: 'Completed' }
  ];

  return (
    <AppLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-text-primary">Tasks</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition"
          >
            <Plus className="w-5 h-5" />
            Create Task
          </button>
        </div>

        {/* Search and Filters */}
        <div className="space-y-4 mb-6">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tasks by title or description..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-border-light focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
            />
          </div>
          
          {/* Filters */}
          <div className="flex gap-4 items-center bg-surface-light p-4 rounded-lg">
            <Filter className="w-5 h-5 text-text-secondary" />
            <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-3 py-1 rounded-lg border border-border-light focus:ring-primary-500 focus:border-primary-500 transition"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as any)}
            className="px-3 py-1 rounded-lg border border-border-light focus:ring-primary-500 focus:border-primary-500 transition"
          >
            <option value="all">All Priority</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
          </div>
        </div>

        {/* Columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {columns.map(({ key, label }) => {
            const columnTasks = filtered.filter((t) => t.status === key);
            
            return (
              <div key={key}>
                <h2 className="text-lg font-medium text-text-primary mb-2">{label}</h2>
                <div className="space-y-4">
                  {columnTasks.length > 0 ? (
                    columnTasks.map((task) => (
                      <div
                        key={task.id}
                        onClick={() => {
                          setSelectedTask(task);
                          setShowDetailsModal(true);
                        }}
                        className="p-4 bg-white rounded-lg shadow-sm border border-gray-100 cursor-pointer hover:bg-gray-50 transition"
                      >
                        {/* Title + Priority */}
                        <div className="flex justify-between items-start">
                          <h3 className="text-base font-medium text-text-primary">
                            {task.title}
                          </h3>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              task.priority === 'high'
                                ? 'bg-error-light/20 text-error-dark'
                                : task.priority === 'medium'
                                ? 'bg-warning-light/20 text-warning-dark'
                                : 'bg-success-light/20 text-success-dark'
                            }`}
                          >
                            {task.priority}
                          </span>
                        </div>

                        {/* Assigned To */}
                        {task.assignedTo?.length ? (
                          <div className="mt-2 text-sm text-text-secondary">
                            <span className="font-medium">Assigned to:</span>{' '}
                            {task.assignedTo.map((uid) => {
                              const u = userMap[uid];
                              return u
                                ? `${u.displayName} (${u.email})`
                                : uid;
                            }).join(', ')}
                          </div>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-text-secondary">
                      No {label.toLowerCase()} tasks
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showCreateModal && (
        <CreateTaskModal onClose={() => setShowCreateModal(false)} onCreate={handleCreateTask} />
      )}

      {showDetailsModal && selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedTask(null);
          }}
        />
      )}
    </AppLayout>
  );
}
